import { Button, Popup, Textarea } from "@garther/ui/components";
import { cn, toast, useI18n } from "@garther/ui/libs";
import { Star } from "lucide-react";
import { useState } from "react";

import { orgApiClient } from "@/libs/api/org-client";

interface LeaveReviewModalProps {
  orgId: string;
  orgName: string;
  open: boolean;
  onClose: () => void;
  onReviewSubmitted: () => void;
}

export function LeaveReviewModal({
  orgId,
  orgName,
  open,
  onClose,
  onReviewSubmitted
}: LeaveReviewModalProps) {
  const { t } = useI18n();
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const MAX_CHARS = 500;

  // Highlight tag options with translation keys
  const HIGHLIGHT_TAGS = [
    { key: "friendlyStaff", value: t("review.highlights.tags.friendlyStaff") },
    { key: "greatVenue", value: t("review.highlights.tags.greatVenue") },
    { key: "deliciousFood", value: t("review.highlights.tags.deliciousFood") },
    { key: "wouldRecommend", value: t("review.highlights.tags.wouldRecommend") },
    { key: "excellentValue", value: t("review.highlights.tags.excellentValue") },
    { key: "wellOrganized", value: t("review.highlights.tags.wellOrganized") }
  ];

  const handleStarClick = (index: number) => {
    setRating(index + 1);
  };

  const handleTagToggle = (tagKey: string) => {
    setSelectedTags((prev) => (prev.includes(tagKey) ? prev.filter((t) => t !== tagKey) : [...prev, tagKey]));
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t("review.validation.ratingRequired"));
      return;
    }
    if (!reviewText.trim()) {
      toast.error(t("review.validation.commentRequired"));
      return;
    }

    // Here you would submit the review to your backend
    console.log({
      rating,
      comment: reviewText,
      highlights: selectedTags
    });

    try {
      const review = await orgApiClient.createReview(orgId, {
        orgId,
        rating,
        comment: reviewText,
        highlights: selectedTags
      });
      console.log(review);
      toast.success(t("review.messages.success"));
      onReviewSubmitted();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(t("review.messages.error"));
      return;
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setReviewText("");
    setSelectedTags([]);
    onClose();
  };

  return (
    <Popup isOpen={open} onClose={handleClose}>
      <div className="max-w-[600px] gap-0 p-0">
        {/* Header */}
        <div className="space-y-0 mb-4">
          <p className="text-responsive-md font-normal text-text-primary">{t("review.title")}</p>
          <p className="text-responsive-sm font-medium text-text-secondary">
            {t("review.subtitle", { orgName })}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Rating Section */}
          <div className="space-y-1">
            <p className="text-responsive-sm font-medium text-text-secondary">
              {t("review.rating.label")} <span className="text-red-500">{t("review.rating.required")}</span>
            </p>
            <div className="flex items-center gap-2">
              {[0, 1, 2, 3, 4].map((index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleStarClick(index)}
                  onMouseEnter={() => setHoverRating(index + 1)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded-sm"
                >
                  <Star
                    className="w-6 h-6"
                    fill={(hoverRating || rating) > index ? "#FBBF24" : "none"}
                    stroke={(hoverRating || rating) > index ? "#FBBF24" : "#E5E7EB"}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Review Textarea */}
          <div className="space-y-0">
            <Textarea
              name="review"
              label={t("review.comment.label")}
              required
              placeholder={t("review.comment.placeholder")}
              value={reviewText}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CHARS) {
                  setReviewText(e.target.value);
                }
              }}
              maxLength={MAX_CHARS}
            />
            <p
              className={cn(
                "text-responsive-sm font-medium text-text-secondary text-right",
                reviewText.length >= MAX_CHARS && "text-red-400"
              )}
            >
              {reviewText.length} / {MAX_CHARS}
            </p>
          </div>

          {/* Highlight Tags */}
          <div className="space-y-1">
            <p className="text-responsive-sm font-medium text-text-secondary">
              {t("review.highlights.label")}
            </p>
            <div className="flex flex-wrap gap-2">
              {HIGHLIGHT_TAGS.map((tag) => (
                <div
                  key={tag.key}
                  className={`text-responsive-base cursor-pointer transition-all rounded-sm px-3 py-1 ${
                    selectedTags.includes(tag.key)
                      ? "bg-background-dark text-text-inverse hover:bg-background-dark/90"
                      : "bg-background-highlight hover:bg-background-highlight/10"
                  }`}
                  onClick={() => handleTagToggle(tag.key)}
                >
                  {tag.value}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSubmit}>{t("review.actions.submit")}</Button>
          </div>
        </div>
      </div>
    </Popup>
  );
}
